import { inject, Injectable } from '@angular/core';
import { WINDOW } from '@app/core/providers/window.provider';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { LogService } from '@app/core/services/log.service';

/**
 * To get Forethought working in a dev server:
 *      1) Ensure your IP address is whitelisted in Forethought
 *      2) Add the embeded api key to your config/forethought.ini
 *
 * Forethought documentation: https://support.forethought.ai/hc/en-us/articles/14239088125459-Solve-Widget-UI-Guide-for-Developers#h_01HXJGY8N0WTT6BAC7KJ9G3S66
 */
@Injectable({
    providedIn: 'root'
})
export class ForethoughtService {
    public readonly forethoughtWidgetClosedEvent: string = 'forethoughtWidgetClosed';
    public readonly forethoughtTrustedOrigin: string = 'forethought.ai';

    private readonly window: Window = inject(WINDOW);
    private readonly logService: LogService = inject(LogService);
    private readonly bigCommerceModeStateService: BigCommerceModeStateService = inject(BigCommerceModeStateService);

    private widgetCloseListenerRegistered: boolean = false;
    private widgetHasBeenOpened: boolean = false;

    public get hasForethoughtSupport(): boolean {
        return 'Forethought' in globalThis;
    }

    public openWidget(): void {
        if (!this.bigCommerceModeStateService.showRestrictedUI() || !this.hasForethoughtSupport) {
            this.logService.error('Forethought is not supported in this environment.');
            return;
        }

        this.widgetHasBeenOpened = true;

        this.registerHideLauncherOnWidgetCloseListener();
        this.openWidgetUi();
    }

    private openWidgetUi(): void {
        Forethought('widget', 'show');
        Forethought('widget', 'open');
    }

    private registerHideLauncherOnWidgetCloseListener(): void {
        if (this.widgetCloseListenerRegistered) {
            return;
        }

        this.window.addEventListener('message', this.onForethoughtMessage);
        this.widgetCloseListenerRegistered = true;
    }

    private readonly onForethoughtMessage: (event: MessageEvent) => void = (event: MessageEvent): void => {
        if (!this.isForethoughtMessageOrigin(event.origin)) {
            return;
        }

        const data = event.data as { event?: string; } | null;
        if (data?.event !== this.forethoughtWidgetClosedEvent || !this.widgetHasBeenOpened) {
            return;
        }

        Forethought('widget', 'hide');
    };

    private isForethoughtMessageOrigin(origin: string): boolean {
        try {
            const hostname = new URL(origin).hostname;

            return hostname === this.forethoughtTrustedOrigin || hostname.endsWith(`.${this.forethoughtTrustedOrigin}`);
        } catch {
            return false;
        }
    }
}
